import * as React from 'react';

import {colors} from '../../colors/colors';
import {LabelWithContent} from '../../typography/label-with-content';
import {useResizeObserver} from '../../utils/hooks/use-resize-observer';

import styles from './page-content-previewable.module.css';

type Props = {
    previewedSectionId?: string | null | undefined;
    getPreview: (sectionId?: string | null | undefined) => React.ReactElement;
    leftContent: React.ReactNode;
    upperContent?: React.ReactNode;
    previewLabel?: string;
    previewBackgroundColor?: string;
    isScrollable?: boolean;
};

export function PageContentPreviewable(props: Props) {
    const {
        previewedSectionId,
        getPreview,
        leftContent,
        previewLabel = 'Preview',
        previewBackgroundColor = colors.plumXlt,
        isScrollable = false,
    } = props;

    const previewContent = getPreview(previewedSectionId);
    const wrapperRef = React.useRef<HTMLDivElement | null | undefined>(null);
    const contentRef = React.useRef<HTMLDivElement | null | undefined>(null);

    const [wrapperWidth, wrapperHeight] = useResizeObserver(wrapperRef.current);
    const [contentWidth, contentHeight] = useResizeObserver(contentRef.current);

    const scale = React.useMemo(() => {
        if (
            !wrapperWidth ||
            !wrapperHeight ||
            !contentWidth ||
            !contentHeight ||
            contentWidth === 0 ||
            contentHeight === 0
        ) {
            return 1;
        }

        const scaleX = wrapperWidth / contentWidth;
        const scaleY = wrapperHeight / contentHeight;

        return Math.min(scaleX, scaleY, 1);
    }, [wrapperWidth, wrapperHeight, contentWidth, contentHeight]);

    return (
        <div className={styles['page-content-previewable-layout']}>
            <div className={styles['page-content']}>{leftContent}</div>
            <div className={styles['preview-content']}>
                {props.upperContent}
                <LabelWithContent
                    label={previewLabel || ''}
                    content={
                        <div
                            // @ts-expect-error
                            ref={wrapperRef}
                            className='flex justify-center align-center br-16 pad-16 full-height min-width-0 min-height-0 pos-relative'
                            style={{
                                backgroundColor: previewBackgroundColor,
                                overflow: isScrollable ? 'scroll' : 'hidden',
                            }}
                        >
                            <div
                                // @ts-expect-error
                                ref={contentRef}
                                style={{
                                    transform: isScrollable ? 'none' : `scale(${scale})`,
                                    height: isScrollable ? '100%' : 'fit-content',
                                }}
                                className={styles['preview-content-inner']}
                            >
                                {previewContent}
                            </div>
                        </div>
                    }
                    isFullHeight={true}
                />
            </div>
        </div>
    );
}
